import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type Crumb = {
  label: string;
  to?: string;
};

const SEGMENT_LABELS: Record<string, { key: string; defaultLabel: string }> = {
  admin: { key: 'admin.breadcrumbs.admin', defaultLabel: 'Admin' },
  users: { key: 'admin.breadcrumbs.users', defaultLabel: 'users' },
  simulations: { key: 'admin.breadcrumbs.simulations', defaultLabel: 'Simulations' },
  payments: { key: 'admin.breadcrumbs.payments', defaultLabel: 'Payments' },
  analytics: { key: 'admin.breadcrumbs.analytics', defaultLabel: 'Analytics' },
  logs: { key: 'admin.breadcrumbs.logs', defaultLabel: 'Logs' },
  prompts: { key: 'admin.breadcrumbs.prompts', defaultLabel: 'Prompts' },
  'test-lab': { key: 'admin.breadcrumbs.testLab', defaultLabel: 'Test Lab' },
  'interview-settings': {
    key: 'admin.breadcrumbs.interviewSettings',
    defaultLabel: 'Interview Defaults',
  },
  support: { key: 'admin.breadcrumbs.support', defaultLabel: 'Support' },
  outreach: { key: 'admin.breadcrumbs.outreach', defaultLabel: 'Outreach' },
  history: { key: 'admin.breadcrumbs.history', defaultLabel: 'history' },
};

function toTitleCase(value: string): string {
  return value
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getDynamicLabel(prevSegment: string | undefined, t: (key: string, opts?: { defaultValue: string }) => string): string {
  if (prevSegment === 'users' || prevSegment === 'simulations') {
    return t('admin.breadcrumbs.detail', { defaultValue: 'Detail' });
  }
  if (prevSegment === 'prompts') return t('admin.breadcrumbs.editor', { defaultValue: 'Editor' });
  return t('admin.breadcrumbs.detail', { defaultValue: 'Detail' });
}

export function AdminBreadcrumbs() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'admin') return null;

  const crumbs: Crumb[] = segments.map((segment, index) => {
    const prev = index > 0 ? segments[index - 1] : undefined;
    const isDynamicId =
      index > 1 &&
      (prev === 'users' || prev === 'simulations' || prev === 'prompts' || prev === 'support') &&
      !SEGMENT_LABELS[segment];

    const mapped = SEGMENT_LABELS[segment];
    const label = isDynamicId
      ? getDynamicLabel(prev, t)
      : mapped
        ? t(mapped.key, { defaultValue: mapped.defaultLabel })
        : toTitleCase(segment);
    const to = index < segments.length - 1 ? `/${segments.slice(0, index + 1).join('/')}` : undefined;

    return { label, to };
  });

  return (
    <nav aria-label={t('admin.breadcrumbs.label', { defaultValue: 'Breadcrumb' })} className="font-mono text-[12px] text-ig-text-tertiary">
      {crumbs.map((crumb, index) => (
        <span key={`${crumb.label}-${index}`}>
          {crumb.to ? (
            <Link className="transition hover:text-ig-text-secondary" to={crumb.to}>
              {crumb.label}
            </Link>
          ) : (
            <span className="text-ig-text-secondary">{crumb.label}</span>
          )}
          {index < crumbs.length - 1 ? <span>{' / '}</span> : null}
        </span>
      ))}
    </nav>
  );
}
